import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { LowerCasePipe } from '@angular/common';
import { RouterModule } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { SvgIcon } from 'app/shared/svg-icon/svg-icon';
import { EmptyDataParticules } from 'app/shared/molecules/empty-data-particules/empty-data-particules';
import { SalesSummary } from 'app/core/models/sales-summary.model';
import { amountFor, formatCurrencyAmount } from 'app/shared/utils/sales-funnel';

/**
 * Where deals were lost.
 *
 * The two indented rows are a PARTITION of the missed count, not siblings of
 * it: denying a quote flips its opportunity to MISSED automatically, so listing
 * "denied quotes" beside "missed opportunities" would count every rejection
 * twice. The indent in the template is what stops a reader adding them to the
 * headline.
 *
 * Stalled opportunities sit below a hairline because they are a different
 * population — still open, still recoverable, not lost at all.
 */
@Component({
  selector: 'primecontrol-lost-card',
  imports: [LowerCasePipe, RouterModule, TranslateModule, SvgIcon, EmptyDataParticules],
  templateUrl: './lost-card.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LostCard {
  readonly summary = input<SalesSummary | null>(null);
  readonly loading = input<boolean>(false);
  /** The summary request failed: an error state, never the "no data" one. */
  readonly error = input<boolean>(false);

  private _translateService: TranslateService = inject(TranslateService);
  private readonly _langChange = toSignal(this._translateService.onLangChange);

  /**
   * Active i18n language. Reads `_langChange` (not just `currentLang`) so
   * that computeds depending on this recompute on a runtime language switch
   * — `currentLang` alone is not a signal and would leave amounts stuck in
   * the previous locale while the `| translate` text moves on.
   */
  private readonly lang = computed<string>(() => {
    this._langChange();
    return this._translateService.currentLang || this._translateService.defaultLang || 'es';
  });

  protected readonly missedCount = computed<number>(() => this.summary()?.lost?.missed?.count ?? 0);

  protected readonly cohortCount = computed<number>(
    () => this.summary()?.funnel?.opportunities?.count ?? 0
  );

  protected readonly afterQuotingCount = computed<number>(
    () => this.summary()?.lost?.missed?.afterQuoting?.count ?? 0
  );

  protected readonly beforeQuotingCount = computed<number>(
    () => this.summary()?.lost?.missed?.beforeQuoting?.count ?? 0
  );

  protected readonly stalledCount = computed<number>(
    () => this.summary()?.lost?.stalledOpportunities ?? 0
  );

  protected readonly topReason = computed<{ reason: string; count: number } | null>(() => {
    const missed = this.summary()?.lost?.missed;
    if (!missed?.topReason) {
      return null;
    }
    return { reason: missed.topReason, count: missed.topReasonCount };
  });

  protected readonly hasData = computed<boolean>(
    () => this.missedCount() > 0 || this.stalledCount() > 0
  );

  /** Denied money, formatted; null when no amount exists for the lead currency. */
  protected readonly afterQuotingAmount = computed<string | null>(() => {
    const currency = this.summary()?.currencies?.find((c) => c.isPrimary) ?? null;
    const amount = amountFor(
      this.summary()?.lost?.missed?.afterQuoting?.amounts ?? [],
      currency?.idCurrency ?? null
    );
    if (amount == null) {
      return null;
    }
    return formatCurrencyAmount(amount, this.lang(), currency?.isoCode ?? '');
  });

  /** Share of the cohort that was lost (0-100), for the headline bar. */
  protected readonly missedShare = computed<number>(() => {
    const cohort = this.cohortCount();
    return cohort > 0 ? Math.min(100, (this.missedCount() / cohort) * 100) : 0;
  });

  /** Whole-number percent of `part` over `whole`; 0 when there is no whole. */
  protected percentOf(part: number, whole: number): number {
    return whole > 0 ? Math.round((part / whole) * 100) : 0;
  }
}
